import {
  ArrowLeft,
  User,
  MapPin,
  Save,
  Receipt,
  CreditCard,
} from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useUpdateBeneficiary } from '@/hooks/useCpn'
import { CpnBeneficiaryUser } from '@/types/cpn'
import {
  CPN_PAYMENT_REASONS,
  PAYMENT_REASON_LABELS,
  type CpnPaymentReason,
} from '@/utils/cpn'
import { logger } from '@/utils/logger'
import {
  METADATA_FIELD_CONFIG,
  validateBenificiaryMetadataField,
  validateUserInfoField,
} from '@/utils/validation'

interface BeneficiaryEditScreenProps {
  beneficiary: CpnBeneficiaryUser
  onBack: () => void
  onSave: () => void
}

interface UserInfoFormData {
  firstName: string
  lastName: string
  street: string
  city: string
  stateProvince: string
  postalCode: string
  country: string
  paymentReason: string
}

interface MetadataFormData {
  ACCOUNT_NUMBER: string
  BANK_NAME: string
  BANK_CODE: string
  BANK_COUNTRY: string
  CLABE: string
  RECIPIENT_ID_NUMBER: string
  SWIFT_CODE: string
}

// Helper function to safely extract metadata values
const getMetadataValue = (
  metadata: Record<string, unknown> | undefined,
  key: string
): string => {
  if (!metadata || typeof metadata[key] !== 'string') {
    return ''
  }
  return metadata[key] as string
}

interface FormErrors {
  [key: string]: string | undefined
}

const BeneficiaryEditScreen = ({
  beneficiary,
  onBack,
  onSave,
}: BeneficiaryEditScreenProps) => {
  const updateBeneficiaryMutation = useUpdateBeneficiary()
  const [userInfoData, setUserInfoData] = useState<UserInfoFormData>({
    firstName: beneficiary.firstName || '',
    lastName: beneficiary.lastName || '',
    street: beneficiary.street || '',
    city: beneficiary.city || '',
    stateProvince: beneficiary.stateProvince || '',
    postalCode: beneficiary.postalCode || '',
    country: beneficiary.country || '',
    paymentReason:
      getMetadataValue(beneficiary.metadata, 'paymentReason') || 'PMT022',
  })

  // Only include metadata fields that have actual values
  const getExistingMetadata = (): Record<string, string> => {
    const existingFields: Record<string, string> = {}
    const allPossibleFields = {
      ACCOUNT_NUMBER: getMetadataValue(beneficiary.metadata, 'ACCOUNT_NUMBER'),
      BANK_NAME: getMetadataValue(beneficiary.metadata, 'BANK_NAME'),
      BANK_CODE: getMetadataValue(beneficiary.metadata, 'BANK_CODE'),
      BANK_COUNTRY: getMetadataValue(beneficiary.metadata, 'BANK_COUNTRY'),
      CLABE: getMetadataValue(beneficiary.metadata, 'CLABE'),
      RECIPIENT_ID_NUMBER: getMetadataValue(
        beneficiary.metadata,
        'RECIPIENT_ID_NUMBER'
      ),
      SWIFT_CODE: getMetadataValue(beneficiary.metadata, 'SWIFT_CODE'),
    }

    // Only include fields that have values
    Object.entries(allPossibleFields).forEach(([key, value]) => {
      if (value && value.trim() !== '') {
        existingFields[key] = value
      }
    })

    return existingFields
  }

  const [metadataData, setMetadataData] = useState<Record<string, string>>(
    getExistingMetadata()
  )

  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleUserInfoChange = (
    name: keyof UserInfoFormData,
    value: string
  ) => {
    setUserInfoData((prev) => ({ ...prev, [name]: value }))

    if (touched[name]) {
      const error = validateUserInfoField(name, value)
      setErrors((prev) => ({ ...prev, [name]: error }))
    }
  }

  const handleMetadataChange = (name: string, value: string) => {
    setMetadataData((prev) => ({ ...prev, [name]: value }))

    if (touched[name]) {
      const error = validateBenificiaryMetadataField(
        name as keyof MetadataFormData,
        value
      )
      setErrors((prev) => ({ ...prev, [name]: error }))
    }
  }

  const handleInputBlur = (name: string) => {
    setTouched((prev) => ({ ...prev, [name]: true }))

    if (name in userInfoData) {
      const error = validateUserInfoField(
        name as keyof UserInfoFormData,
        userInfoData[name as keyof UserInfoFormData]
      )
      setErrors((prev) => ({ ...prev, [name]: error }))
    } else if (name in metadataData) {
      const error = validateBenificiaryMetadataField(
        name as keyof MetadataFormData,
        metadataData[name as keyof MetadataFormData]
      )
      setErrors((prev) => ({ ...prev, [name]: error }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}
    let isValid = true

    // Validate user info fields
    ;(Object.keys(userInfoData) as Array<keyof UserInfoFormData>).forEach(
      (key) => {
        const error = validateUserInfoField(key, userInfoData[key])
        if (error) {
          newErrors[key] = error
          isValid = false
        }
      }
    )

    // Validate metadata fields (only those that exist)
    Object.keys(metadataData).forEach((key) => {
      const error = validateBenificiaryMetadataField(
        key as keyof MetadataFormData,
        metadataData[key]
      )
      if (error) {
        newErrors[key] = error
        isValid = false
      }
    })

    setErrors(newErrors)
    setTouched({
      ...Object.keys(userInfoData).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      ),
      ...Object.keys(metadataData).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      ),
    })
    return isValid
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      // Extract paymentReason from userInfoData
      const { paymentReason, ...userInfo } = userInfoData

      // Update user info (without paymentReason)
      await updateBeneficiaryMutation.mutateAsync({
        beneficiaryId: beneficiary.uuid,
        data: {
          updateType: 'user',
          ...userInfo,
        },
      })

      // Update metadata (include existing fields and paymentReason)
      const metadata = {
        ...metadataData, // All existing metadata fields (already filtered to non-empty)
        paymentReason,
      }

      if (Object.keys(metadata).length > 0) {
        await updateBeneficiaryMutation.mutateAsync({
          beneficiaryId: beneficiary.uuid,
          data: {
            updateType: 'metadata',
            metadata,
          },
        })
      }
      onSave() // TanStack Query will automatically update the cache
    } catch (error) {
      logger.error('Failed to update beneficiary', error)
      alert('Failed to update beneficiary. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const hasErrors = Object.values(errors).some((error) => error)

  // Function to render dynamic metadata fields
  const renderMetadataFields = () => {
    const metadataKeys = Object.keys(metadataData)

    if (metadataKeys.length === 0) {
      return null // Don't show the section if no metadata exists
    }

    return (
      <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
        <div className="flex items-center space-x-2 mb-3">
          <CreditCard className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <h3 className="font-medium text-gray-900 dark:text-white">
            Bank Information
          </h3>
        </div>

        <div className="space-y-3">
          {metadataKeys.map((fieldKey) => {
            const config = METADATA_FIELD_CONFIG[fieldKey]
            if (!config) return null

            return (
              <div key={fieldKey}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {config.label}
                </label>
                <Input
                  value={metadataData[fieldKey] || ''}
                  onChange={(e) => {
                    const value = config.toUpperCase
                      ? e.target.value.toUpperCase()
                      : e.target.value
                    handleMetadataChange(fieldKey, value)
                  }}
                  onBlur={() => handleInputBlur(fieldKey)}
                  placeholder={config.placeholder}
                  maxLength={config.maxLength}
                  className={
                    errors[fieldKey]
                      ? 'border-red-500 dark:border-red-400'
                      : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                  }
                />
                {errors[fieldKey] && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {errors[fieldKey]}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </Card>
    )
  }

  return (
    <div className="flex flex-col h-[600px] dark:bg-gray-900">
      <div className="p-4 space-y-4 flex-1 overflow-y-auto dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center space-x-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="p-1">
            <ArrowLeft className="w-5 h-5 dark:text-gray-300" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              Edit Recipient
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Update recipient information
            </p>
          </div>
        </div>

        {/* Personal Information */}
        <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center space-x-2 mb-3">
            <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <h3 className="font-medium text-gray-900 dark:text-white">
              Personal Information
            </h3>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  First Name *
                </label>
                <Input
                  value={userInfoData.firstName}
                  onChange={(e) =>
                    handleUserInfoChange('firstName', e.target.value)
                  }
                  onBlur={() => handleInputBlur('firstName')}
                  placeholder="John"
                  className={
                    errors.firstName
                      ? 'border-red-500 dark:border-red-400'
                      : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                  }
                />
                {errors.firstName && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {errors.firstName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Name *
                </label>
                <Input
                  value={userInfoData.lastName}
                  onChange={(e) =>
                    handleUserInfoChange('lastName', e.target.value)
                  }
                  onBlur={() => handleInputBlur('lastName')}
                  placeholder="Doe"
                  className={
                    errors.lastName
                      ? 'border-red-500 dark:border-red-400'
                      : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                  }
                />
                {errors.lastName && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {errors.lastName}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Payment Reason */}
        <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center space-x-2 mb-3">
            <Receipt className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <h3 className="font-medium text-gray-900 dark:text-white">
              Payment Reason
            </h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Reason for Payment *
            </label>
            <Select
              value={userInfoData.paymentReason}
              onValueChange={(value) => {
                handleUserInfoChange('paymentReason', value)
                setTouched((prev) => ({ ...prev, paymentReason: true }))
              }}
            >
              <SelectTrigger
                className={`w-full dark:bg-gray-900 dark:border-gray-600 dark:text-white ${
                  errors.paymentReason
                    ? 'border-red-500 dark:border-red-400'
                    : touched.paymentReason && !errors.paymentReason
                      ? 'border-green-500 dark:border-green-400'
                      : ''
                }`}
              >
                <SelectValue placeholder="Select payment reason" />
              </SelectTrigger>
              <SelectContent className="dark:bg-gray-900 dark:border-gray-600 max-h-[300px] max-w-[365px]">
                {CPN_PAYMENT_REASONS.map((code) => (
                  <SelectItem
                    key={code}
                    value={code}
                    className="dark:text-white dark:focus:bg-gray-700 dark:focus:text-white text-sm"
                  >
                    {PAYMENT_REASON_LABELS[code as CpnPaymentReason]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.paymentReason && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                {errors.paymentReason}
              </p>
            )}
          </div>
        </Card>

        {/* Address Information */}
        <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center space-x-2 mb-3">
            <MapPin className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <h3 className="font-medium text-gray-900 dark:text-white">
              Address Information
            </h3>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Street Address *
              </label>
              <Input
                value={userInfoData.street}
                onChange={(e) => handleUserInfoChange('street', e.target.value)}
                onBlur={() => handleInputBlur('street')}
                placeholder="123 Main Street"
                className={
                  errors.street
                    ? 'border-red-500 dark:border-red-400'
                    : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                }
              />
              {errors.street && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                  {errors.street}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  City *
                </label>
                <Input
                  value={userInfoData.city}
                  onChange={(e) => handleUserInfoChange('city', e.target.value)}
                  onBlur={() => handleInputBlur('city')}
                  placeholder="Toronto"
                  className={
                    errors.city
                      ? 'border-red-500 dark:border-red-400'
                      : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                  }
                />
                {errors.city && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {errors.city}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  State/Province *
                </label>
                <Input
                  value={userInfoData.stateProvince}
                  onChange={(e) =>
                    handleUserInfoChange('stateProvince', e.target.value)
                  }
                  onBlur={() => handleInputBlur('stateProvince')}
                  placeholder="Ontario"
                  className={
                    errors.stateProvince
                      ? 'border-red-500 dark:border-red-400'
                      : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                  }
                />
                {errors.stateProvince && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {errors.stateProvince}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Postal Code *
                </label>
                <Input
                  value={userInfoData.postalCode}
                  onChange={(e) =>
                    handleUserInfoChange('postalCode', e.target.value)
                  }
                  onBlur={() => handleInputBlur('postalCode')}
                  placeholder="M5V 3A8"
                  className={
                    errors.postalCode
                      ? 'border-red-500 dark:border-red-400'
                      : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                  }
                />
                {errors.postalCode && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {errors.postalCode}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Country Code *
                </label>
                <Input
                  value={userInfoData.country}
                  onChange={(e) =>
                    handleUserInfoChange(
                      'country',
                      e.target.value.toUpperCase()
                    )
                  }
                  onBlur={() => handleInputBlur('country')}
                  placeholder="CA"
                  maxLength={2}
                  className={
                    errors.country
                      ? 'border-red-500 dark:border-red-400'
                      : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                  }
                />
                {errors.country && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {errors.country}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Dynamic Bank Information - only shows fields that have values */}
        {renderMetadataFields()}
      </div>

      {/* Save Button */}
      <div className="p-4 border-t border-gray-100 dark:border-gray-700 dark:bg-gray-900">
        <Button
          onClick={handleSubmit}
          disabled={hasErrors || isSubmitting}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white h-10 text-sm font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Saving Changes...</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <Save className="w-4 h-4 dark:text-white" />
              <span>Save Changes</span>
            </div>
          )}
        </Button>
      </div>
    </div>
  )
}

export default BeneficiaryEditScreen
